import { z } from "zod";

/**
 * HITL review contract — the shapes exchanged between the admin-ui and
 * the orchestrator's `/api/jobs/:id/draft-batch` and `/api/jobs/:id/review`
 * endpoints.
 *
 * The review flow is per-item: a reviewer can approve or reject each
 * draft row independently and may submit a partial batch (returning later
 * for the rest). `audit_log` is append-only — once a row has a
 * `review_decision_recorded` event, it is locked and cannot be re-decided
 * (the orchestrator returns 409 `already_decided` on conflict). The
 * job-level rollup to `jobs.review_decision` only fires when every row in
 * `validatorResult.artifacts[0].scoredRows` has been decided across one or
 * more submits.
 *
 * Decision rationale is captured at
 * `../../../../docs/decisions/2026-05-03-hitl-review-flow.md`,
 * including the eager-promotion revisit section that introduced
 * `existingDecisionPromoted` and `promotionPending` below.
 */

/**
 * One row in the draft batch as the reviewer sees it. Fields mirror the
 * corresponding columns in `tools_draft` (curator + validator metadata)
 * with the validator's per-row score overlay surfaced explicitly so the
 * UI can highlight flagged items.
 *
 * `existingDecision` is non-null when this row was decided in a prior
 * submit — the UI renders it as locked. The decision is taken from the
 * latest `review_decision_recorded` audit row for the row's `draftId`.
 */
export const draftBatchItemSchema = z.object({
  /** The `tools_draft` `id` column — the slug-style business key. */
  draftId: z.string().min(1),

  // Identifying fields
  name: z.string(),
  vendor: z.string(),
  url: z.string(),
  category: z.string(),
  subcategory: z.string().nullable(),
  problemTags: z.array(z.string()),
  pricingModel: z.string(),
  /** Sentinel `-1` means "contact sales"; mirror-exact from the Sheet. */
  pricingFrom: z.number().int(),
  /**
   * The curator's own description of what the tool does and how it
   * does it — mirror of `tools_draft.embedding_text`. Surfaced so the
   * review-row card can show the same prose the catalogue/drafts
   * expand-on-click renders, per the 2026-06-01 operator feedback
   * (issue #218). Nullable for legacy drafts written before the
   * curator emitted embedding text.
   */
  embeddingText: z.string().nullable(),

  // Curator-appended fields (m1-4-1)
  confidenceScore: z.number().nullable(),
  sourceCount: z.number().int().nullable(),

  // Validator-appended fields (m1-4-1)
  qualityScore: z.number().nullable(),
  validatorFlag: z.boolean().nullable(),
  qualityNotes: z.string().nullable(),

  // Decision overlay (m1-4-2 — partial-submit support)
  existingDecision: z.enum(["approved", "rejected"]).nullable(),
  existingDecisionAt: z.string().datetime().nullable(),
  existingDecisionBy: z.string().nullable(),

  /**
   * Whether the approved row has been promoted to `tools_and_services`.
   *
   *   `null`  — not yet decided, OR decided as rejected (promotion is N/A).
   *   `true`  — decided as approved AND row is in `tools_and_services`.
   *   `false` — decided as approved AND row is NOT yet in `tools_and_services`
   *             (an eager append failed; the rollup safety-net will retry).
   *
   * Computed by the orchestrator from a one-shot
   * `tools_and_services!A2:A` read on every GET. Lets the review panel
   * render `decided · approved · promoted` (green) versus
   * `decided · approved · pending` (amber) for already-decided rows.
   */
  existingDecisionPromoted: z.boolean().nullable(),
});

export type DraftBatchItem = z.infer<typeof draftBatchItemSchema>;

/**
 * The GET `/api/jobs/:id/draft-batch` response. `jobStatus` lets the UI
 * branch when the job is no longer in `pending_review` (e.g. the rollup
 * fired and the job is now `completed` or `cancelled`); the panel still
 * renders the item list for read-only inspection in that case.
 */
export const draftBatchResponseSchema = z.object({
  jobId: z.string().uuid(),
  /**
   * Friendly auto-incrementing handle for the job — surfaced so the
   * review queue's row links can use the canonical `/jobs/<n>/review`
   * URL form without a separate fetch. UUID `jobId` remains the
   * authoritative key on every audit row and FK; `jobNumber` is purely
   * a display affordance. See `packages/db/src/schema.ts` `jobs.jobNumber`.
   */
  jobNumber: z.number().int().positive(),
  jobStatus: z.string(),
  reviewDeadline: z.string().datetime().nullable(),
  items: z.array(draftBatchItemSchema),
});

export type DraftBatchResponse = z.infer<typeof draftBatchResponseSchema>;

/**
 * The GET `/api/review/pending` response — the cross-job queue. Returns
 * every `pending_review` job's draft batch in a single bundled response
 * so the admin-ui's `/review` page can render a stacked-panel inbox
 * without N round-trips. Each entry mirrors the per-job
 * `draftBatchResponseSchema` exactly so the same `<ReviewPanel>`
 * component renders both surfaces.
 *
 * Ordering is `created_at DESC` (newest pending batch first). Empty
 * pending state returns `{ batches: [] }` with status 200.
 *
 * Note: the orchestrator may silently skip a `pending_review` job whose
 * `validatorResult` is malformed (no parsable `artifacts[0]`) — this is
 * the documented defensive filter so a single anomaly doesn't 500 the
 * whole inbox. The per-job `GET /api/jobs/:id/draft-batch` still 409s
 * (`not_yet_reviewable`) on the same row, so the operator can still
 * surface and fix it. See `services/orchestrator/README.md` §
 * "GET /api/review/pending".
 */
export const pendingReviewResponseSchema = z.object({
  batches: z.array(draftBatchResponseSchema),
});

export type PendingReviewResponse = z.infer<typeof pendingReviewResponseSchema>;

/**
 * The POST `/api/jobs/:id/review` body. At least one decision is
 * required — empty submits are 400. Re-deciding an already-decided row
 * is rejected at the handler with 409 `already_decided`.
 */
export const reviewSubmitBodySchema = z.object({
  decisions: z
    .array(
      z.object({
        draftId: z.string().min(1),
        decision: z.enum(["approved", "rejected"]),
        notes: z.string().optional(),
      }),
    )
    .min(1),
});

export type ReviewSubmitBody = z.infer<typeof reviewSubmitBodySchema>;

/**
 * The POST `/api/jobs/:id/review` response. `decidedCount` /
 * `remainingCount` cover the whole batch (across all submits, not just
 * this call), letting the UI render "X of N decided" without a follow-up
 * fetch. `reviewDecision` is non-null only when the rollup fired this
 * submit — the rollup rule is "approved if any approved, rejected if all
 * rejected" (see `services/orchestrator/src/routes/review.ts`).
 *
 * `promotionPending` carries the count of approved rows in **this**
 * submit that didn't promote eagerly to `tools_and_services` — usually
 * `0`. A non-zero value means the eager `appendCatalogueRows` call
 * threw (transient Sheets failure); the decision is still recorded and
 * the rollup safety-net will re-attempt the append at batch
 * completion. The UI uses this to render a transient "N row(s)
 * approved — promotion pending; will be retried at batch completion"
 * warning beneath the SubmitBar.
 */
export const reviewSubmitResponseSchema = z.object({
  decidedCount: z.number().int().nonnegative(),
  remainingCount: z.number().int().nonnegative(),
  jobStatus: z.string(),
  reviewDecision: z.enum(["approved", "rejected"]).nullable(),
  promotionPending: z.number().int().nonnegative(),
});

export type ReviewSubmitResponse = z.infer<typeof reviewSubmitResponseSchema>;
